/**
 * Pure UA-based detection for browser/OS combinations known to time out on the
 * Pollinator video stack. The 2026-05-08 join-failure cluster surfaced two
 * production fingerprints — Safari 26.4 and 26.5 on macOS — both showing
 * sustained 30-second `audioVideoDidStart` timeouts driven by Safari's
 * stricter privacy-restricted ICE candidate filtering. Same users joined in
 * <2 seconds on Chrome/Firefox. Safari 26.3 is verified working (Matias-Jofre
 * downgraded 26.4 → 26.3.1 and joined in 2 seconds on the same network), so
 * the rule explicitly excludes 26.3 and below.
 *
 * This function answers: "Should we show a pre-Join warning?" — used BEFORE the
 * user clicks Join, so they switch browsers without first wasting 30 seconds on
 * a doomed handshake. The reactive post-timeout nudge (getJoinFailureGuidance)
 * still fires for users who slip through detection.
 *
 * Returns `{ isKnownBad: false }` for any unrecognized UA — defaults to silent.
 *
 * The body string interpolates the detected version (e.g. "Safari 26.4" or
 * "Safari 26.5") so the user sees the exact version their browser reports.
 * Builds credibility — a user on 26.5 reading "we've seen problems on 26.4"
 * would dismiss the warning as not applying to them.
 *
 * Scope is intentionally narrow:
 *   - Only Safari 26.4 and 26.5 on macOS are flagged today. 26.3 is verified
 *     working; other versions (26.0, 26.6+, 27.x) are excluded until production
 *     data shows they share the same ICE-gathering regression. Widen here if a
 *     cohort report surfaces sustained timeouts on a different version.
 *   - Safari on iOS is out of scope (different WebKit lifecycle, no production
 *     failures observed).
 *   - Chrome 14x on Windows 10 is out of scope (one production data point —
 *     Tiyan Baker — insufficient signal to add a Windows banner; revisit if
 *     the post-deploy cohort report shows a sustained Windows-Chrome pattern).
 *   - Edge is out of scope (Chromium-based, includes "Safari/" in UA for
 *     back-compat — distinguished by trailing "Edg/...").
 */

export type KnownBadBrowserResult = {
  isKnownBad: boolean;
  title?: string;
  body?: string;
};

// Engine-based pre-filter: anything advertising a Chromium-family product
// (Chrome, Edg, OPR, Brave's distinguishing token) uses Blink, not WebKit, and
// is unaffected by the Safari ICE regression. WebKit-only Safari is what we're
// after; on macOS it reports as "Version/X.Y Safari/...".
const CHROMIUM_PRODUCTS = ["Chrome/", "CriOS/", "Edg/", "OPR/", "EdgiOS/", "FxiOS/"];

const SAFARI_VERSION_RE = /Version\/(\d+)\.(\d+)/;

// Production fingerprints from the 2026-05-08 cluster (and known versions
// adjacent to the failure window):
//   - 26.4 fails (Vivienne, Jaqui, Bill, Matias-Jofre baseline)
//   - 26.5 fails (Galit Aloni — joined on Firefox 150 after 26.5 timeouts)
//   - 26.3 works (Matias-Jofre downgrade)
const KNOWN_BAD_SAFARI_MINORS = new Set([4, 5]);

function isSafariOnMacOS(ua: string): boolean {
  // Macintosh marker (frozen by Apple at "Intel Mac OS X 10_15_7" for
  // anti-fingerprinting since ~2020 — present on every macOS Safari UA today)
  if (!/Macintosh/.test(ua)) return false;
  // Exclude iOS-shaped UAs even if they spoof Macintosh (rare on Safari but
  // common on iPadOS-as-macOS for Chrome derivatives)
  if (/iPhone|iPad|iPod/.test(ua)) return false;
  // Exclude any Chromium-family browser that ships "Safari/" in the trailing
  // back-compat token
  if (CHROMIUM_PRODUCTS.some((p) => ua.includes(p))) return false;
  // What's left should be real WebKit Safari
  return /Safari\//.test(ua);
}

function safariVersion(ua: string): { major: number; minor: number } | null {
  const m = ua.match(SAFARI_VERSION_RE);
  if (!m) return null;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  if (!Number.isFinite(major) || !Number.isFinite(minor)) return null;
  return { major, minor };
}

export function detectKnownBadBrowser(userAgent: string): KnownBadBrowserResult {
  if (!userAgent) return { isKnownBad: false };

  // Rule 1: Safari 26.4 or 26.5 on macOS — known to hang at the WebRTC handshake.
  if (isSafariOnMacOS(userAgent)) {
    const version = safariVersion(userAgent);
    if (
      version !== null &&
      version.major === 26 &&
      KNOWN_BAD_SAFARI_MINORS.has(version.minor)
    ) {
      const versionString = `${version.major}.${version.minor}`;
      return {
        isKnownBad: true,
        title: "Safari may not work for this meeting",
        body: `We've seen connection problems on Safari ${versionString}. For the best chance of joining quickly, please use Firefox or Chrome to open this meeting.`,
      };
    }
  }

  return { isKnownBad: false };
}
